import { useMemo, useState } from 'react';

import { api } from '../../../services/api';
import type { Chat } from '../../../types';

type Props = {
  chats: Chat[];
};

const BroadcastPanel = ({ chats }: Props) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [delay, setDelay] = useState(3000);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<
    { chatId: string; success: boolean; error?: string }[] | null
  >(null);

  const eligible = useMemo(
    () => chats.filter((chat) => chat.consent === 'opted_in'),
    [chats],
  );

  const filtered = eligible.filter((chat) =>
    chat.title.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleSelect = (chatId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map((chat) => chat.id)));
  const deselectAll = () => setSelected(new Set());

  const handleSend = async () => {
    if (selected.size === 0 || !message.trim()) return;
    setSending(true);
    setResults(null);
    try {
      const output = await api.bulkSend({
        chatIds: [...selected],
        message: message.trim(),
        delay,
      });
      setResults(output.results);
    } catch {
      setResults([]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="broadcast-panel">
      <p className="tool-description">
        Verstuur een bericht naar meerdere contacts tegelijk. Alleen contacts met toestemming worden getoond.
      </p>
      <input
        type="search"
        className="tool-search"
        placeholder="Contacts doorzoeken…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="broadcast-actions">
        <button type="button" className="button secondary small" onClick={selectAll}>
          Alles
        </button>
        <button type="button" className="button secondary small" onClick={deselectAll}>
          Niets
        </button>
        <span className="muted small">{selected.size} geselecteerd</span>
      </div>
      <div className="broadcast-chat-list">
        {filtered.length === 0 && (
          <p className="empty-state small">Geen in aanmerking komende contacts.</p>
        )}
        {filtered.map((chat) => (
          <label key={chat.id} className="broadcast-chat-item">
            <input
              type="checkbox"
              checked={selected.has(chat.id)}
              onChange={() => toggleSelect(chat.id)}
            />
            <span className="broadcast-chat-name">{chat.title}</span>
          </label>
        ))}
      </div>
      <label className="tool-field">
        <span>Bericht</span>
        <textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Schrijf je bericht…"
          rows={4}
          maxLength={4096}
        />
      </label>
      <label className="tool-field">
        <span>Vertraging tussen berichten (ms)</span>
        <input
          type="number"
          value={delay}
          onChange={(event) => setDelay(Number(event.target.value))}
          min={500}
          max={60000}
          step={500}
        />
      </label>
      <button
        type="button"
        className="button primary small"
        disabled={sending || selected.size === 0 || !message.trim()}
        onClick={handleSend}
      >
        {sending ? 'Versturen…' : `Versturen naar ${selected.size}`}
      </button>
      {results && (
        <div className="broadcast-results">
          <h4>Resultaten</h4>
          {results.length === 0 && <p className="muted small">Er is een fout opgetreden.</p>}
          {results.map((result, index) => {
            const chat = chats.find((c) => c.id === result.chatId);
            return (
              <div
                key={index}
                className={`broadcast-result ${result.success ? 'success' : 'error'}`}
              >
                <span>{chat?.title ?? result.chatId}</span>
                <span>{result.success ? '✓' : `✗ ${result.error ?? ''}`}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BroadcastPanel;
